import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { rolesService } from '@/api/domains/authorization';
import { roleSchema, type RoleFormData } from '@/schemas/authorization/roleSchema';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import type { RoleDetail } from '@/api/domains/authorization/roles.types';


interface UseRoleFormOptions {
  roleId?: string;
  onSuccess?: () => void;
}

export interface UseRoleFormResult {
  form: ReturnType<typeof useForm<RoleFormData>>;
  roleDetail: RoleDetail | null;
  isReady: boolean;
  isLoading: boolean;
  onSubmit: (data: RoleFormData) => Promise<void>;
}

export const useRoleForm = ({ roleId, onSuccess }: UseRoleFormOptions): UseRoleFormResult => {
  const { t: translate } = useTranslation();
  const { closeSidePanel, showNotification, showApiError } = useUI();
  const [roleDetail, setRoleDetail] = useState<RoleDetail | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(roleId));

  const form = useForm<RoleFormData>({
    resolver: zodResolver(roleSchema),
  });

  const { setValue, watch } = form;

  const name = watch('name');
  const isReady = Boolean(name?.trim());

  useEffect(() => {
    const fetchRole = async () => {
      if (!roleId) return;

      setIsLoading(true);
      try {
        const response = await rolesService.getRoleById(roleId);
        if (response.success && response.data) {
          setRoleDetail(response.data);
          setValue('name', response.data.name);
          if (response.data.description) {
            setValue('description', response.data.description);
          }
        } else {
          showApiError(response);
          closeSidePanel();
        }
      } catch {
        showNotification('error', translate('common:errors.unexpected'));
        closeSidePanel();
      } finally {
        setIsLoading(false);
      }
    };

    void fetchRole();
  }, [roleId, setValue, showApiError, showNotification, closeSidePanel, translate]);

  const onSubmit = async (data: RoleFormData) => {
    try {
      let response;
      if (roleId) {
        response = await rolesService.updateRole(roleId, data);
      } else {
        response = await rolesService.createRole(data);
      }

      if (response.success) {
        const successMessage = roleId
          ? translate('authorization:roles.messages.updateSuccess')
          : translate('authorization:roles.messages.createSuccess');
        showNotification('success', successMessage);

        window.dispatchEvent(new CustomEvent('role-form-success'));

        if (onSuccess) {
          onSuccess();
        }
      } else {
        if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, form);
          return;
        }
        showApiError(response);
      }
    } catch {
      showNotification('error', translate('common:errors.unexpected'));
    }
  };

  return { form, roleDetail, isReady, isLoading, onSubmit };
};
